// The `task` wire shape as the DASHBOARD reads it — `GET /v1/tasks`, and the
// `tasks` topic of `/v1/streams`, which re-emits exactly this row when a task's
// version changes.
//
// ## This is NOT the MCP `task` body, and the two must not be merged
//
// `packages/mcp/src/army/task.ts`'s `maskTask` answers a different question for
// a different caller, and the key sets say so. The REST row carries
// `outcome_kind`, `hold_reason`, `plan_id`, `depth` and spells the timestamp
// `created_at`; the MCP row carries `priority`, `depends_on`, `origin`, `sink`,
// `updated_iso`, spells the timestamp `created_iso`, and names the backlog
// depth `queue_depth`. Neither is a subset of the other.
//
// One is the operator's board — where does this row sit in the queue, what is
// holding it, which epic is it part of. The other is how an AGENT asks after
// work it dispatched — what did I ask for, what does it depend on, where does
// the answer go. Forcing them onto one resource would have to add fields to one
// door or drop them from the other, and `/v1` is a published contract. Two
// doors, two shapes, stated deliberately rather than discovered later.
//
// What IS shared is settled where it belongs: `taskLifecycleBucket` derives the
// coarse bucket for both, and `TaskFailureKind` states the survives-recovery
// semantic once.

import { z } from 'zod';
import { MoveItemStateSchema } from '../entities/runner/move-verdict';
import {
  OutcomeKindSchema,
  type Task,
  TaskFailureKindSchema,
  TaskHoldReasonSchema,
  TaskLifecycleBucketSchema,
  type TaskStatus,
  TaskStatusSchema,
  taskLifecycleBucket,
} from '../entities/task';
import { TaskEnvelopeSchema } from '../value-objects/task-envelope';
import { type TaskRunState, TaskRunStateSchema } from '../value-objects/task-run-event/run-state';
import { defineResource } from './resource';

/**
 * The row plus the two facts resolved OUTSIDE it.
 *
 * `planId` comes from `plan_groups` (batched on the list/SSE path, point-read on
 * detail) and `queue` from the account-wide queue view, because neither is a
 * column on `tasks` and folding them into the row read would mean touching a
 * star-select the store deliberately leaves alone.
 */
export type TaskEntity = {
  task: {
    id: string;
    status: TaskStatus;
    outcomeKind: z.infer<typeof OutcomeKindSchema>;
    holdReason: z.infer<typeof TaskHoldReasonSchema> | null;
    failureKind: z.infer<typeof TaskFailureKindSchema> | null;
    attempt: number;
    runnerId: string | null;
    runId: string | null;
    /** The machine a send targeted (#6767); absent or null when none. */
    targetRunnerId?: string | null | undefined;
    targetUntil?: Date | null | undefined;
    /**
     * The task's newest move between machines (#6768), resolved beside the row
     * (`readTaskMoves`); absent or null when it was never moved.
     */
    move?: TaskMoveEntity | null | undefined;
    createdAt: Date;
    envelope: { repo_full_name: string; title: string };
  };
  /** The epic (`plan_groups`) this task belongs to; null for a plain task. */
  planId: string | null;
  /**
   * The account-wide queue standing. Omitted on the DETAIL path, where both
   * fields project as null: a place in line is a list concern, not a single-row
   * one.
   */
  queue?: { position: number | null; depth?: number | null };
};

/** A task's newest move (#6768), as the list read resolves it. */
export type TaskMoveEntity = {
  batchId: string;
  fromRunnerId: string | null;
  toRunnerId: string | null;
  state: string;
  restUntil: Date;
};

/** The published body. Field names and nullability are the existing contract. */
export const TaskWireSchema = z.object({
  id: z.string(),
  repo_full_name: z.string(),
  title: z.string(),
  status: TaskStatusSchema,
  /** Coarse bucket derived from the fine status by `taskLifecycleBucket`, never hand-mapped. */
  lifecycle_bucket: TaskLifecycleBucketSchema,
  /** 1-based rank among the account's `pending` tasks; null for any non-pending row. */
  queue_position: z.number().nullable(),
  /** The account's full pending backlog depth. Account-global, stamped on every row. */
  depth: z.number().nullable(),
  outcome_kind: OutcomeKindSchema,
  /**
   * Why this task is held rather than merely queued (#1899), null when nothing
   * holds it. Only ever non-null on a `pending` row.
   */
  hold_reason: TaskHoldReasonSchema.nullable(),
  /**
   * The kind of the LAST failed attempt, null for a row no failure lane ever
   * touched. NOT gated on a terminal status: it SURVIVES RECOVERY, so a
   * `completed` row that took two tries reports the kind it recovered from.
   * Read it against the row's own `status` — read without one it looks like
   * corruption and is not.
   */
  failure_kind: TaskFailureKindSchema.nullable(),
  /** Failed attempts so far; `0` for a row that has never failed. */
  attempt: z.number(),
  runner_id: z.string().nullable(),
  run_id: z.string().nullable(),
  plan_id: z.string().nullable(),
  /**
   * The machine this task was SENT to (#6767, `POST /v1/runners/:id/work`): only it may
   * claim the task until `target_until`; null when nobody sent it, or once released. The
   * server always sends both; OPTIONAL on the parse so a client deployed ahead of the api
   * (the dashboard validates every `tasks` stream row with this schema) still accepts the
   * rows of an api that does not send them yet.
   */
  target_runner_id: z.string().nullable().optional(),
  /** When the send's hard target ends (send time + 10 min), ISO-8601; null with no target. */
  target_until: z.string().nullable().optional(),
  /**
   * The task's newest move between machines (#6768): its batch, the two machines, the item's
   * state (`checkpointing` / `moving` / `landed` / `failed`) and when the job may move again
   * (`rest_until`, ISO). Null when it was never moved. OPTIONAL on the parse for the reason
   * `target_runner_id` gives (#6767: a required key made a dashboard rolled ahead drop rows).
   */
  move: z
    .object({
      batch_id: z.string(),
      from_runner_id: z.string().nullable(),
      to_runner_id: z.string().nullable(),
      state: MoveItemStateSchema,
      rest_until: z.string(),
    })
    .nullable()
    .optional(),
  created_at: z.string(),
});

/** What a client should type against for a task row. */
export type TaskWire = z.infer<typeof TaskWireSchema>;

export const TaskResource = defineResource<TaskEntity, typeof TaskWireSchema>({
  schema: TaskWireSchema,
  serialize: ({ task: t, planId, queue }) => ({
    id: t.id,
    repo_full_name: t.envelope.repo_full_name,
    title: t.envelope.title,
    status: t.status,
    lifecycle_bucket: taskLifecycleBucket(t.status),
    queue_position: queue?.position ?? null,
    depth: queue?.depth ?? null,
    outcome_kind: t.outcomeKind,
    hold_reason: t.holdReason,
    failure_kind: t.failureKind,
    attempt: t.attempt,
    runner_id: t.runnerId,
    run_id: t.runId,
    plan_id: planId,
    target_runner_id: t.targetRunnerId ?? null,
    target_until: t.targetUntil?.toISOString() ?? null,
    move: taskMoveWire(t.move),
    created_at: t.createdAt.toISOString(),
  }),
});

/** A task's move on the wire; null when it was never moved. A stored state this build does not know reads null. */
function taskMoveWire(m: TaskMoveEntity | null | undefined): TaskWire['move'] {
  if (m === null || m === undefined) return null;
  const state = MoveItemStateSchema.safeParse(m.state);
  if (!state.success) return null;
  return {
    batch_id: m.batchId,
    from_runner_id: m.fromRunnerId,
    to_runner_id: m.toRunnerId,
    state: state.data,
    rest_until: m.restUntil.toISOString(),
  };
}

// ── the DETAIL body (`GET /v1/tasks/:id`) ────────────────────────────────────
//
// REST-only, and deliberately so: the MCP `task` verb answers the same question
// with `maskTask`, whose key set this file's header already argues must not be
// merged with the queue row's. What follows is the detail body's own shape, and
// it exists as a `Resource` for a reason `openapi.ts` states about itself —
// bodies are published only through a route's `response: { kind: 'entity' }`,
// whose rule is that the body IS a resource exactly. `projectTaskDetail`
// (`apps/api/src/routes/tasks.ts`) was the LAST call site in the product that
// composed a body as `{...XResource.serialize(e), …extra}`, so this is also
// where that shape ends.

/**
 * The stored `failure_kind`, narrowed against THIS build's vocabulary.
 *
 * A kind written by a worker built on a richer taxonomy — or by a hand-edited
 * row — answers `null` rather than being passed through, because `null` is the
 * only value that keeps a `failure_kind` claim honest when the schema rejects
 * the stored string (#2574 C5 read seam).
 *
 * It lives here, beside the schema it narrows against, because BOTH task-shaped
 * REST bodies need it and they are in different files: the detail body below,
 * and the attempt-ladder row (`projectTaskAttempt`,
 * `apps/api/src/routes/tasks.ts`). Two copies of a read-seam narrow is how one
 * of them stops agreeing with the vocabulary it claims to publish.
 */
export function narrowTaskFailureKind(
  raw: string | null,
): z.infer<typeof TaskFailureKindSchema> | null {
  if (raw === null) return null;
  const parsed = TaskFailureKindSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * The run pulse the detail body carries — the LAST event seen on the task's
 * current run, or `null` when no run has started.
 *
 * Declared structurally rather than imported: the producer is
 * `TaskRunStatusView` in `@developerz/mcp`, and `domain` imports nothing
 * internal. A `TaskRunStatusView` satisfies this (it carries one extra field,
 * `taskId`, which the wire has no key for and must not grow one — the caller
 * already knows the id it asked about).
 *
 * `state` is the ONE verdict (#5918), computed once by the producer. There is no
 * `finished` here on purpose: every wire's `finished` is projected from `state` by
 * {@link runFinished}, so nothing between the producer and the wire can read a
 * boolean that calls a released, errored or silent run live.
 */
export type TaskRunPulseEntity = {
  runId: string;
  lastSeq: number | null;
  lastKind: string | null;
  lastAtIso: string | null;
  state: TaskRunState;
};

/**
 * What the detail projection reads: the stored row in full, the epic it belongs
 * to, the run pulse, and the ONE sentence for its failure.
 *
 * `failureReason` is passed IN rather than derived here. The sentence itself is
 * not authored in any app: it is the `unclaimed` / `ran` pair of the kind's row
 * `copy` (`FailureKindRow`, `entities/task/failure-kinds/row.ts`), read through
 * `taskFailureCopy`. The api's `failureReasonSentence`
 * (`apps/api/src/routes/tasks.ts`) only picks the arm by `attempt`, and the
 * worker's `humanFailureReason` reads the same row's `alert` copy — one table,
 * no restatement. What stays out of this file is that arm choice, which is a
 * route's reading of the row, not part of the wire shape this file owns.
 *
 * The invariant between the two IS enforced here, though: see `serialize`.
 */
export type TaskDetailEntity = {
  task: Task;
  /** The epic (`plan_groups`) this task belongs to; null for a plain task. */
  planId: string | null;
  /** The sentence for {@link TaskDetailEntity.task}'s `failureKind`, or null. */
  failureReason: string | null;
  run: TaskRunPulseEntity | null;
};

/** The run pulse as the wire spells it. `null` until a run starts. */
export const TaskRunPulseWireSchema = z.object({
  run_id: z.string(),
  last_seq: z.number().nullable(),
  last_kind: z.string().nullable(),
  last_at: z.string().nullable(),
  /**
   * {@link runFinished} of `state`, and nothing more: the run signed off with
   * `run_finished`. `false` does NOT mean live — read `state` for that. Kept because
   * readers that predate `state` branch on it.
   */
  finished: z.boolean(),
  /** Where the run is — the field to render. See `TASK_RUN_STATES`. */
  state: TaskRunStateSchema,
});

export type TaskRunPulseWire = z.infer<typeof TaskRunPulseWireSchema>;

/**
 * The legacy `finished` flag of a run pulse, derived from its `state`. Every door that
 * serves a pulse derives it here: {@link taskRunPulseWire} (the task detail body and
 * the admin radar) and the control-MCP `task run_status` verb
 * (`packages/mcp/src/army/task.ts`), which spells its other keys its own way.
 */
export function runFinished(state: TaskRunState): boolean {
  return state === 'finished';
}

/**
 * THE REST projection of a run pulse — the task detail body and the admin radar
 * (`apps/api/src/routes/admin-fleet.ts`) both answer through it, so those two doors
 * spell it the same way. The MCP `task run_status` verb keeps its own spelling
 * (`last_at_iso`, plus `task_id`) and shares only {@link runFinished}.
 */
export function taskRunPulseWire(run: TaskRunPulseEntity): TaskRunPulseWire {
  return {
    run_id: run.runId,
    last_seq: run.lastSeq,
    last_kind: run.lastKind,
    last_at: run.lastAtIso,
    finished: runFinished(run.state),
    state: run.state,
  };
}

/**
 * The detail body: the queue row, plus the scoped-plan detail the list
 * summarises away, plus the run pulse.
 *
 * Two reuses, both deliberate:
 *
 * - it EXTENDS {@link TaskWireSchema}, so a field added to the queue row cannot
 *   leave the detail behind (`GET /v1/tasks` and `GET /v1/tasks/:id` answered
 *   with the same eleven keys by hand before this, and nothing said they had to
 *   keep agreeing);
 * - the seven envelope fields are PICKED off `TaskEnvelopeSchema` rather than
 *   restated. They are the same values under the same names, so restating them
 *   would be a second declaration of a shape this repo already has — and the
 *   published constraints (`problem` max 10,000, `confidence` 0..1, the `origin`
 *   and `sink` enums) then describe the body accurately for free.
 *
 * Still a projection, never the stored row dumped whole: `account_id` is the
 * caller's own, `dispatched_by` names a credential, `lease_epoch` is the
 * internal fence token, and `llm` names a BYOK pool key — none of them is the
 * client's business, and none has a key here.
 */
export const TaskDetailWireSchema = TaskWireSchema.extend({
  ...TaskEnvelopeSchema.pick({
    origin: true,
    sink: true,
    confidence: true,
    problem: true,
    acceptance_criteria: true,
    files_touched: true,
    forbidden: true,
  }).shape,
  /** Scheduling priority class; lower runs first WITHIN a class. */
  priority: z.number(),
  /** Sibling task ids that must finish before this is ready. */
  depends_on: z.array(z.string()),
  updated_at: z.string(),
  /**
   * ONE human sentence for `failure_kind` — `null` when there is none, never
   * `''`. A refusal at attempt 0 reads differently from a ladder-exhausted
   * failure under the SAME kind; collapsing them is the defect #2574 exists to
   * end, so the sentence is a field of its own rather than a rendering of the
   * kind.
   */
  failure_reason: z.string().nullable(),
  run: TaskRunPulseWireSchema.nullable(),
});

/** What a client should type against for a task detail body. */
export type TaskDetailWire = z.infer<typeof TaskDetailWireSchema>;

export const TaskDetailResource = defineResource<TaskDetailEntity, typeof TaskDetailWireSchema>({
  schema: TaskDetailWireSchema,
  serialize: ({ task: t, planId, failureReason, run }) => {
    // It overrides the queue row's pass-through spelling on purpose — the
    // detail body is the one that carries `failure_reason` beside it, and a
    // reason for a kind nobody can name is the collapse the pair exists to
    // avoid.
    const failureKind = narrowTaskFailureKind(t.failureKind);
    return {
      // No `queue` argument: `queue_position`/`depth` project null here. A place
      // in line is a LIST concern — the standing is computed account-wide, never
      // for a single-row read (#513).
      ...TaskResource.serialize({ task: t, planId }),
      priority: t.priority,
      origin: t.envelope.origin,
      sink: t.envelope.sink,
      confidence: t.envelope.confidence,
      problem: t.envelope.problem,
      acceptance_criteria: t.envelope.acceptance_criteria,
      files_touched: t.envelope.files_touched,
      forbidden: t.envelope.forbidden,
      depends_on: t.dependsOn,
      updated_at: t.updatedAt.toISOString(),
      failure_kind: failureKind,
      // The invariant, stated in ONE place: no sentence without a kind this
      // build knows. The api detail route (`apps/api/src/routes/tasks.ts`)
      // narrows first and passes no sentence for an unknown kind; this gate
      // holds the pair together for any other caller, which could otherwise
      // ship a reason for a `failure_kind: null` row.
      failure_reason: failureKind === null ? null : failureReason,
      run: run === null ? null : taskRunPulseWire(run),
    };
  },
});
